'use client';

import React, { useRef } from 'react';
import type { NavigationNode } from '@/hooks/useNavigationMenu';

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

interface MenubarProps {
  menus: NavigationNode[];
  activeId: number | null;
  setActiveId: (id: number) => void;
  onKeyNav: (e: React.KeyboardEvent<HTMLLIElement>, idx: number) => void;
  id: string;
  onLeafClick?: (path: string) => void;
}

export function Menubar({
  menus,
  activeId,
  setActiveId,
  onKeyNav,
  id,
  onLeafClick,
}: MenubarProps) {
  const ulRef = useRef<HTMLUListElement>(null);

  return (
    <ul
      id={id}
      ref={ulRef}
      role="menubar"
      aria-orientation="vertical"
      className="font-medium text-gray-700"
    >
      {menus.map((menu, idx) => {
        const isActive = activeId === menu.id;
        const isLeaf = menu.children.length === 0;
        return (
          <li
            key={menu.id}
            role="menuitem"
            aria-haspopup={!isLeaf}
            aria-expanded={!isLeaf ? isActive : undefined}
            aria-controls={!isLeaf ? `${id}-panel-${menu.id}` : undefined}
            tabIndex={0}
            data-menu-index={idx}
            onMouseEnter={() => setActiveId(menu.id)}
            onClick={() => {
              if (isLeaf && onLeafClick) {
                onLeafClick(menu.path);
              } else {
                setActiveId(menu.id);
              }
            }}
            onKeyDown={(e) => {
              if (isLeaf && (e.key === 'Enter' || e.key === ' ') && onLeafClick) {
                e.preventDefault();
                onLeafClick(menu.path);
              } else {
                onKeyNav(e, idx);
              }
            }}
            className={cx(
              'cursor-pointer px-2 py-3 outline-none focus:ring-2 focus:ring-blue-200',
              isActive && 'bg-blue-50 rounded-md'
            )}
          >
            {menu.title}
          </li>
        );
      })}
    </ul>
  );
}
